import React, { PureComponent } from 'react';

// original author: vlin
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { HotKeys } from 'react-hotkeys';

import Tooltip from './Tooltip';
import { getAdditionalProps } from '@eb/eds-utils';
import { onClickOutsideHOC } from '@eb/eds-utils';
import {
    TOOLTIP_CONTENT_PROPTYPE,
    ARROW_LOCATION_PROPTYPE,
    STYLE_PROPTYPE,
} from './constants';

import { HIDE_CHILD, SHOW_CHILD } from '@eb/eds-hot-keys';
import { ACTION_KEY_MAP } from './hotKeys';

/**
 * withTooltip is an HOC that provides the wrappedComponent with a tooltip.
 *
 * To use the HOC, wrap the component like so:
 *   let ButtonWithTooltip = withTooltip(Button);
 *
 * Then, provide the following props through the wrappedComponent
 *
 * e.g. <ButtonWithTooltip tooltipText="A button"> This is Button Text </ButtonWithTooltip>
 * 1) tooltipText             - required
 * 2) arrowLocation           - optional
 * 3) tooltipStyle            - optional
 * 4) hideTooltipOnMouseLeave - optional
 *
 * @summary An HOC that provides the wrappedComponent with an accompanying tooltip
 */
const withTooltip = (Component) =>
    class WithTooltipWrapper extends PureComponent {
        static propTypes = {
            /**
             * The text to be set as id for tooltip
             **/
            tooltipId: PropTypes.string.isRequired,

            /**
             * The text or React element to be displayed.
             * If text is provided, it must be a label, no sentences, with a max character count of 32.
             **/
            tooltipText: TOOLTIP_CONTENT_PROPTYPE.isRequired,

            /**
             * Location of arrow for tooltip relative to targetNode. The direction chosen
             * relates to which side of the Tooltip the arrow will appear.
             * Options: left, right, top, bottom
             **/
            tooltipArrowLocation: ARROW_LOCATION_PROPTYPE,

            /**
             * The background color of the Tooltip. Options: basic, phab
             **/
            tooltipStyle: STYLE_PROPTYPE,

            /**
             * If true, the tooltip will hide anytime the mouse leaves the
             * targetNode whether the targetNode has focus or not.
             **/
            hideTooltipOnMouseLeave: PropTypes.bool,

            /**
             * Wrapper component tag. Defaults to DIV
             **/
            wrapperComponent: PropTypes.string,

            __containerClassName: PropTypes.string,
        };

        state = {
            targetNode: null,
        };

        hotKeyHandlers = {
            [HIDE_CHILD]: () => this._handleKeyPressedHide(),
            [SHOW_CHILD]: (e) => this._handleFocus(e),
        };

        _handleMouseOver = (e) => {
            this._mouseOverNode = true;

            this._handleShow(e);
        };

        _handleMouseLeave = () => {
            this._mouseOverNode = false;

            if (
                (!this._nodeFocused || this.props.hideTooltipOnMouseLeave) &&
                !this._mouseOverNode
            ) {
                this._handleHide();
            }
        };

        _handleFocus = (e) => {
            this._nodeFocused = true;

            this._handleShow(e);
        };

        _handleKeyPressedHide = () => {
            if (this._nodeFocused || this._mouseOverNode) {
                this._nodeFocused = true;
                this._mouseOverNode = false;
                this._handleHide();
            }
        };

        _handleReset = () => {
            if (this._nodeFocused || this._mouseOverNode) {
                this._nodeFocused = false;
                this._mouseOverNode = false;
                this._handleHide();
            }
        };

        _handleBlur = () => {
            this._nodeFocused = false;

            if (!this._nodeFocused && !this._mouseOverNode) {
                this._handleHide();
            }
        };

        _handleShow = (e) => {
            this.setState({
                targetNode: e.currentTarget,
            });
        };

        _handleHide = () => {
            this.setState({
                targetNode: null,
            });
        };

        render() {
            const { targetNode } = this.state;
            const {
                tooltipId,
                tooltipText,
                tooltipArrowLocation,
                tooltipStyle,
                wrapperComponent,
                __containerClassName,
            } = this.props;
            const componentProps = getAdditionalProps(this);
            const tooltipClasses = classNames(
                'eds-tooltip--hoc-wrapper',
                __containerClassName,
            );
            let describedBy;

            // We don't want `aria-describedby` on the div when no tooltip is
            // present
            if (targetNode) {
                describedBy = tooltipId;
            }

            return (
                <HotKeys
                    component={wrapperComponent}
                    keyMap={ACTION_KEY_MAP}
                    handlers={this.hotKeyHandlers}
                >
                    <ToolTipWrapper
                        tooltipClasses={tooltipClasses}
                        onMouseOver={this._handleMouseOver}
                        onMouseLeave={this._handleMouseLeave}
                        onFocus={this._handleFocus}
                        onBlur={this._handleBlur}
                        onClickOutside={this._handleReset}
                        describedBy={describedBy}
                    >
                        <Component {...componentProps} />
                        <Tooltip
                            tooltipId={tooltipId}
                            targetNode={targetNode}
                            arrowLocation={tooltipArrowLocation}
                            style={tooltipStyle}
                        >
                            {tooltipText}
                        </Tooltip>
                    </ToolTipWrapper>
                </HotKeys>
            );
        }
    };

const ToolTipWrapper = onClickOutsideHOC(
    ({
        tooltipClasses,
        onMouseOver,
        onMouseLeave,
        onFocus,
        onClick,
        onBlur,
        describedBy,
        children,
    }) => (
        /* Accessibility: This component does have key events, but not picked up by the linter becasue of the HOC */
        /* Ideally this component would be a button element but as it is a HOC, that has already been relased we can't do this. e.g. copy button with tooltip */
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
        <div
            className={tooltipClasses}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseLeave}
            onFocus={onFocus}
            onClick={onClick}
            onBlur={onBlur}
            // `aria-describedby` is based on the <button> tag and not
            // with the component because in cases where component has
            // `aria-hidden` (i.e. <i> ), we still want `aria-describedby`
            // to be present and corresponds to the tooltip id
            aria-describedby={describedBy}
            data-spec="tooltip-wrapper"
            tabIndex="0"
            role="tooltip"
        >
            {children}
        </div>
    ),
);

export default withTooltip;
